import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep, isEqual } from 'lodash-es';
import { Fragment, type JSX, useState } from 'react';
import { Form } from 'react-bootstrap';
import { generatePath } from 'react-router';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { countryOptions, getStateFieldLabel, getStateFieldOptions } from 'Config/CountryStateList';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { THIRD_PARTIES_AND_SERVICES_LISTING } from 'Config/PathTemplates';
import { TPRM_THIRD_PARTY_CONTACT_EMAIL, TPRM_THIRD_PARTY_CONTACT_NAME, TPRM_THIRD_PARTY_CONTACT_PHONE_NUMBER, TPRM_THIRD_PARTY_EIN, TPRM_THIRD_PARTY_MANAGER, TPRM_THIRD_PARTY_NAME } from 'Config/Tooltips';
import { validateEin, validateEmail } from 'Helpers/InputValidation';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { ValidationError } from 'Models/ErrorTypes';
import { CreateThirdPartyRequest, FinalizationState, ThirdPartyContact, ThirdPartyForNonTprmUser, ThirdPartyResponse, UpdateThirdPartyRequest } from 'Models/TPRM';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import { DeleteThirdPartyModal } from './DeleteThirdPartyModal';
import styles from './ManageThirdPartyForm.module.css';

type baseProps = {
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    tprmApi: ClientTPRMApi;
};

export type ManageThirdPartyFormProps = baseProps &
    (
        | {
              thirdParty?: never;
              thirdPartyExists: false;
              userHasFullTprmAccess: false;
          }
        | {
              thirdParty: ThirdPartyForNonTprmUser;
              thirdPartyExists: true;
              userHasFullTprmAccess: false;
          }
        | {
              thirdParty?: never;
              thirdPartyExists: false;
              userHasFullTprmAccess: true;
          }
        | {
              thirdParty: ThirdPartyResponse;
              thirdPartyExists: true;
              userHasFullTprmAccess: true;
          }
    );

interface FormFieldsState {
    country?: string;
    name?: string;
    thirdPartyManagerUserId?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    ein?: string;
    state?: string;
    thirdPartyContacts: ThirdPartyContact[];
    website?: string;
    zipCode?: string;
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft' | 'createAndSubmit' | 'createAndFinalize';
type RequestActionConfirmationNeeded = 'delete';

export type RequestAndModalState =
    | {
          requestState: 'idle';
          displayedModal?: 'confirmFinalizeNewThirdParty' | 'confirmDelete';
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          showToast: boolean;
          displayedModal?: never;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          displayedModal?: 'confirmFinalizeNewThirdParty' | 'confirmDelete';
      }
    | {
          requestState: 'failure';
          errorMessage: string;
          showToast: boolean;
      };

/**
 * Renders a form for creating a new third party, or updating or deleting an existing third party.
 */
export const ManageThirdPartyForm = (props: ManageThirdPartyFormProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle' });
    const [thirdPartyManager, setThirdPartyManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.thirdParty?.vendor_manager_user_id)); // Keeps track of the currently selected User in the Third-Party Manager drop-down menu.

    // TODO: Extract these form fields into their own states.
    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        country: props.thirdParty?.country,
        name: props.thirdParty?.name,
        thirdPartyManagerUserId: props.thirdParty?.vendor_manager_user_id,
        addressLine1: props.thirdParty?.address_line_1,
        addressLine2: props.thirdParty?.address_line_2,
        city: props.thirdParty?.city,
        ein: props.thirdParty?.ein,
        state: props.thirdParty?.state,
        thirdPartyContacts: props.thirdParty?.vendor_contacts || [],
        website: props.thirdParty?.website,
        zipCode: props.thirdParty?.zip_code,
    });
    const [tags, setTags] = useState<string[]>(props.thirdParty ? props.thirdParty.tags : []);

    const disabled = requestAndModalState.requestState === 'inProgress' || requestAndModalState.requestState === 'success';

    const createThirdParty = async (finalizationState: FinalizationState, action: 'createAsDraft' | 'createAndSubmit'): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: action });

        try {
            validateForm();
            await props.tprmApi.createThirdParty(buildRequestForNewThirdParty(finalizationState));
            setRequestAndModalState({ requestState: 'success', completedAction: action, showToast: true });
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateThirdParty = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit' });

        try {
            validateForm();
            await props.tprmApi.updateThirdParty(buildRequestForExistingThirdParty(), props.thirdParty!.id);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', showToast: true });
        } catch (err) {
            handleRequestError(err);
        }
    };

    /** Build the request object for creating a new Third Party. This ensures only necessary fields are included in the request to the API. */
    const buildRequestForNewThirdParty = (finalizationState: FinalizationState): CreateThirdPartyRequest => {
        const request: CreateThirdPartyRequest = {
            finalization_state: finalizationState,
            country: formFieldsState.country!,
            name: formFieldsState.name!,
            vendor_manager_user_id: formFieldsState.thirdPartyManagerUserId!,
            tags: tags,
            vendor_contacts: cloneDeep(formFieldsState.thirdPartyContacts), // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        };

        if (formFieldsState.addressLine1) {
            request.address_line_1 = formFieldsState.addressLine1;
        }
        if (formFieldsState.addressLine2) {
            request.address_line_2 = formFieldsState.addressLine2;
        }
        if (formFieldsState.city) {
            request.city = formFieldsState.city;
        }
        if (formFieldsState.ein) {
            request.ein = formFieldsState.ein;
        }
        if (formFieldsState.state) {
            request.state = formFieldsState.state;
        }
        if (formFieldsState.website) {
            request.website = formFieldsState.website;
        }
        if (formFieldsState.zipCode) {
            request.zip_code = formFieldsState.zipCode;
        }

        let third_party_contacts = request.vendor_contacts;
        third_party_contacts = filterEmptyThirdPartyContacts(third_party_contacts);
        third_party_contacts = convertEmptyThirdPartyContactAttributesToUndefined(third_party_contacts);
        request.vendor_contacts = third_party_contacts;

        return request;
    };

    // TODO: Remove `Undefined` from the backend and build this like any other request in the app.
    /** Build the request object for updating an existing Third Party. This ensures only necessary fields are included in the request to the API. */
    const buildRequestForExistingThirdParty = (): UpdateThirdPartyRequest => {
        const request: UpdateThirdPartyRequest = {};
        if (props.thirdParty!.country !== formFieldsState.country) {
            request.country = formFieldsState.country;
        }
        if (props.thirdParty!.name !== formFieldsState.name) {
            request.name = formFieldsState.name;
        }
        if (props.thirdParty!.vendor_manager_user_id !== formFieldsState.thirdPartyManagerUserId) {
            request.vendor_manager_user_id = formFieldsState.thirdPartyManagerUserId;
        }
        if (props.thirdParty!.address_line_1 !== formFieldsState.addressLine1) {
            request.address_line_1 = formFieldsState.addressLine1;
        }
        if (props.thirdParty!.address_line_2 !== formFieldsState.addressLine2) {
            request.address_line_2 = formFieldsState.addressLine2;
        }
        if (props.thirdParty!.city !== formFieldsState.city) {
            request.city = formFieldsState.city;
        }
        if (props.thirdParty!.ein !== formFieldsState.ein) {
            request.ein = formFieldsState.ein;
        }
        if (props.thirdParty!.state !== formFieldsState.state) {
            request.state = formFieldsState.state;
        }
        if (props.thirdParty!.tags !== tags) {
            request.tags = tags;
        }
        if (!isEqual(props.thirdParty!.vendor_contacts.sort(), formFieldsState.thirdPartyContacts?.sort())) {
            request.vendor_contacts = convertEmptyThirdPartyContactAttributesToUndefined(filterEmptyThirdPartyContacts(cloneDeep(formFieldsState.thirdPartyContacts))); // cloneDeep() is used so that we can set empty strings to undefined in the next step without modifying the original state, which would throw a React error about switching from controlled to uncontrolled components.
        }
        if (props.thirdParty!.website !== formFieldsState.website) {
            request.website = formFieldsState.website;
        }
        if (props.thirdParty!.zip_code !== formFieldsState.zipCode) {
            request.zip_code = formFieldsState.zipCode;
        }
        return request;
    };

    /** Discard any Third-Party contacts where all attributes are "empty." */
    const filterEmptyThirdPartyContacts = (thirdPartyContacts: ThirdPartyContact[]): ThirdPartyContact[] => {
        return thirdPartyContacts.filter((thirdPartyContact) => {
            return Object.values(thirdPartyContact).some((value) => {
                if (typeof value === 'string' && value.trim().length === 0) {
                    return false;
                }
                return true;
            });
        });
    };

    /**
     * Set any attributes of Third-Party contacts that are "empty" strings to undefined before submitting the request.
     * This is necessary because the API will return an error if email_address is an empty string. The other attributes are included simply for completeness.
     */
    const convertEmptyThirdPartyContactAttributesToUndefined = (thirdPartyContacts: ThirdPartyContact[]): ThirdPartyContact[] => {
        thirdPartyContacts.forEach((thirdPartyContact) => {
            if ((thirdPartyContact.email_address?.trim().length ?? 0) === 0) {
                thirdPartyContact.email_address = undefined;
            }
            if ((thirdPartyContact.name?.trim().length ?? 0) === 0) {
                thirdPartyContact.name = undefined;
            }
            if ((thirdPartyContact.phone_number?.trim().length ?? 0) === 0) {
                thirdPartyContact.phone_number = undefined;
            }
            if ((thirdPartyContact.additional_information?.trim().length ?? 0) === 0) {
                thirdPartyContact.additional_information = undefined;
            }
        });
        return thirdPartyContacts;
    };

    // TODO [TPRM - managing third parties and services]: Define a reusable component for managing third-party contacts. Include logic and tests for things like validating email addresses and discarding empty contacts before saving.
    /** Perform input validation. */
    const validateForm = (): void => {
        // Validate required attributes.
        if (!formFieldsState.name) {
            throw new ValidationError('Name is required.');
        }
        if (!formFieldsState.thirdPartyManagerUserId) {
            throw new ValidationError('Manager is required.');
        }
        if (!formFieldsState.country) {
            throw new ValidationError('Country/Region is required.');
        }

        // Validate optional attributes.
        if (formFieldsState.ein && !validateEin(formFieldsState.ein)) {
            throw new ValidationError('Employer ID Number must only contain numbers and dashes.');
        }

        formFieldsState.thirdPartyContacts.forEach((thirdPartyContact) => {
            if (thirdPartyContact.email_address && !validateEmail(thirdPartyContact.email_address)) {
                throw new ValidationError('Invalid email address.');
            }
        });
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        setRequestAndModalState({ requestState: 'failure', errorMessage: error.message, showToast: true });
    };

    /** Handles changes made to general text fields. */
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    /** Handles changes made to general drop-down (select) fields. */
    const handleSelectChange = (value: ChangeEventType, formFieldId: string): void => {
        if (formFieldId === 'country') {
            setFormFieldsState({ ...formFieldsState, country: value as string | undefined, state: '' });
        } else {
            setFormFieldsState({ ...formFieldsState, [formFieldId]: value });
        }
    };

    /** Handles changes made to the Third-Party Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user !== thirdPartyManager) {
            if (user === undefined) {
                setThirdPartyManager(undefined);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
            } else {
                setThirdPartyManager(user);
                setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
            }
        }
    };

    /** Adds the fields (empty) for a new Third-Party contact. */
    const handleAddThirdPartyContact = (): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts.push({ name: '', email_address: '', phone_number: '', additional_information: '' });
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Deletes an existing Third-Party contact. */
    const handleDeleteThirdPartyContact = (index: number): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts.splice(index, 1);
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Handles changes made to an existing Third-Party contact. */
    const handleThirdPartyContactChange = (index: number, attribute: 'name' | 'email_address' | 'phone_number' | 'additional_information', value: string): void => {
        const thirdPartyContacts = [...formFieldsState.thirdPartyContacts];
        thirdPartyContacts[index] = {
            ...thirdPartyContacts[index],
            [attribute]: value,
        };
        setFormFieldsState({ ...formFieldsState, thirdPartyContacts: thirdPartyContacts });
    };

    /** Renders a field for selecting the state, region, territory, province, etc. of the selected country. */
    const renderStateField = (): JSX.Element => {
        const formFieldLabel = getStateFieldLabel(formFieldsState.country);

        if (!formFieldsState.country) {
            // If a country has not yet been selected, return a disabled text field.
            return <FormFieldText value="Select a Country first" disabled formFieldType="text" formFieldId="state" formFieldLabel={formFieldLabel} required={false} />;
        }

        const stateFieldOptions = getStateFieldOptions(formFieldsState.country);

        if (stateFieldOptions === undefined) {
            // If the selected country does not have corresponding states/etc. from which to select, return a free-form text field.
            // The value defaults to the empty string to avoid transitioning the component from uncontrolled to controlled.
            return <FormFieldText value={formFieldsState.state || ''} formFieldType="text" handleChange={handleChange} formFieldId="state" formFieldLabel={formFieldLabel} required={false} />;
        }

        // If the selected country has corresponding states/etc. from which to select, return a select component with those values.
        // The value defaults to null to avoid transitioning the component from uncontrolled to controlled, and to clear out the selection when a new country is selected.
        return <FormFieldSelect selectedOption={formFieldsState.state || null} options={stateFieldOptions} handleChange={handleSelectChange} formFieldId="state" formFieldLabel={formFieldLabel} />;
    };

    const saveButtons = (() => {
        const thirdPartyIsBeingCreated = props.thirdParty === undefined;

        if (props.userHasFullTprmAccess && thirdPartyIsBeingCreated) {
            return (
                <>
                    <Button variant="primary" fontAwesomeImage={ICON_SAVE} isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === 'createAsDraft'} loadingText="Saving..." onClick={() => createThirdParty(FinalizationState.DRAFT_IN_PROGRESS, 'createAsDraft')}>
                        Save as draft
                    </Button>
                    <Button variant="primary" fontAwesomeImage={faCheck} isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === 'createAndSubmit'} loadingText="Saving..." onClick={() => createThirdParty(FinalizationState.FINALIZED, 'createAndSubmit')}>
                        Save and finalize
                    </Button>
                </>
            );
        } else {
            const action = props.thirdParty ? 'edit' : 'createAsDraft';

            return (
                <Button
                    variant="primary"
                    fontAwesomeImage={ICON_SAVE}
                    isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === action}
                    loadingText="Saving..."
                    onClick={() => {
                        if (props.thirdParty) {
                            updateThirdParty();
                        } else {
                            createThirdParty(FinalizationState.DRAFT_IN_PROGRESS, 'createAsDraft');
                        }
                    }}
                >
                    {props.userHasFullTprmAccess ? 'Save' : 'Save as draft'}
                </Button>
            );
        }
    })();

    const toast: JSX.Element = (() => {
        switch (requestAndModalState.requestState) {
            case 'idle':
            case 'inProgress':
                return <></>;
            case 'failure':
                return requestAndModalState.showToast ? <TextToast variant="failure" clearToast={() => setRequestAndModalState({ ...requestAndModalState, showToast: false })} text={requestAndModalState.errorMessage} /> : <></>;
            case 'success':
                switch (requestAndModalState.completedAction) {
                    case 'createAsDraft':
                    case 'createAndSubmit':
                    case 'edit':
                    case 'createAndFinalize':
                        return requestAndModalState.showToast ? <LinkButtonToast variant="success" clearToast={() => setRequestAndModalState({ ...requestAndModalState, showToast: false })} linkButtonText="View third parties and third-party services" linkButtonTo={generatePath(THIRD_PARTIES_AND_SERVICES_LISTING)} text="Third party saved." /> : <></>;
                    case 'delete':
                        return <></>;
                }
        }
    })();

    const headerTitle = props.thirdParty ? 'Manage Third Party' : 'Create Third Party';

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.displayedModal === 'confirmDelete' && props.thirdPartyExists && <DeleteThirdPartyModal exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} requestAndModalState={requestAndModalState} setRequestAndModalState={setRequestAndModalState} thirdParty={props.thirdParty!} tprmApi={props.tprmApi} userHasFullTprmAccess={props.userHasFullTprmAccess} />}
            {toast}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(THIRD_PARTIES_AND_SERVICES_LISTING)}>Third Parties and Services</BreadcrumbLink>
                        <BreadcrumbText>{headerTitle}</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={headerTitle}
                body={[
                    {
                        content: (
                            <Form noValidate>
                                <fieldset disabled={disabled}>
                                    {props.thirdParty ? <Text variant="Header2">Manage Third Party</Text> : <Text variant="Header2">Create Third Party</Text>}
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldText value={formFieldsState.name || ''} formFieldType="text" handleChange={handleChange} formFieldId="name" formFieldLabel="Legal Name" required tooltip={TPRM_THIRD_PARTY_NAME} />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldText value={formFieldsState.website || ''} formFieldType="text" handleChange={handleChange} formFieldId="website" formFieldLabel="Website URL" required={false} />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldUserSelect rolesRestriction={TPRM_ROLES} selectedUser={thirdPartyManager} onUserSelected={handleSelectUserChange} formFieldId="thirdPartyManagerUserId" formFieldLabel="Manager" tooltip={TPRM_THIRD_PARTY_MANAGER} required />
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldSelect selectedOption={formFieldsState.country} options={countryOptions} handleChange={handleSelectChange} formFieldId="country" formFieldLabel="Country/Region" required />
                                    </div>
                                    <div className={styles.formFieldGroup}>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText value={formFieldsState.addressLine1 || ''} formFieldType="text" handleChange={handleChange} formFieldId="addressLine1" formFieldLabel="Address Line 1" required={false} />
                                        </div>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText value={formFieldsState.addressLine2 || ''} formFieldType="text" handleChange={handleChange} formFieldId="addressLine2" formFieldLabel="Address Line 2" required={false} />
                                        </div>
                                    </div>
                                    <div className={styles.formFieldGroup}>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText value={formFieldsState.city || ''} formFieldType="text" handleChange={handleChange} formFieldId="city" formFieldLabel="City" required={false} />
                                        </div>
                                        <div className={styles.formFieldContainer}>{renderStateField()}</div>
                                    </div>
                                    <div className={styles.formFieldGroup}>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText value={formFieldsState.zipCode || ''} formFieldType="text" handleChange={handleChange} formFieldId="zipCode" formFieldLabel="Zip Code" required={false} />
                                        </div>
                                        <div className={styles.formFieldContainer}>
                                            <FormFieldText value={formFieldsState.ein || ''} formFieldType="text" handleChange={handleChange} formFieldId="ein" formFieldLabel="Employer ID Number" tooltip={TPRM_THIRD_PARTY_EIN} />
                                        </div>
                                    </div>
                                    <div className={styles.formFieldContainer}>
                                        <FormFieldMultiOptionSelect
                                            defaultSelectedOptions={cachedData
                                                .getTagCategoriesGroupedOptions()
                                                .map((group) => group.options)
                                                .flat()
                                                .filter((option) => tags.includes(option.value as string))}
                                            formFieldLabel="Tags"
                                            formFieldId="tags"
                                            handleChange={(value: GroupOptionType[]) => setTags(value.map((tag) => tag.value as string))}
                                            options={cachedData.getTagCategoriesGroupedOptions()}
                                            accessibilityLabel="Tags"
                                        />
                                    </div>
                                    <Text variant="Header3" color="darkGray">
                                        Third-Party Contact Information
                                    </Text>
                                    {formFieldsState.thirdPartyContacts.map((thirdPartyContact, index) => (
                                        <Fragment key={index}>
                                            <div className={styles.formFieldGroup}>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'name', event.currentTarget.value)} formFieldId={`thirdPartyContactName${index}`} formFieldLabel={`Name`} tooltip={TPRM_THIRD_PARTY_CONTACT_NAME} value={thirdPartyContact.name || ''} />
                                                </div>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="email" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'email_address', event.currentTarget.value)} formFieldId={`thirdPartyContactEmailAddress${index}`} formFieldLabel={`Email Address`} tooltip={TPRM_THIRD_PARTY_CONTACT_EMAIL} value={thirdPartyContact.email_address || ''} />
                                                </div>
                                                <div className={styles.formFieldContainer}>
                                                    <FormFieldText formFieldType="text" handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'phone_number', event.currentTarget.value)} formFieldId={`thirdPartyContactPhoneNumber${index}`} formFieldLabel={`Phone Number`} tooltip={TPRM_THIRD_PARTY_CONTACT_PHONE_NUMBER} value={thirdPartyContact.phone_number || ''} />
                                                </div>
                                                <div className={styles.trashIconContainer}>
                                                    <IconButton aria-label={`delete third-party contact ${index}`} onClick={() => handleDeleteThirdPartyContact(index)} fontAwesomeImage={ICON_DELETE_REMOVE} />
                                                </div>
                                            </div>
                                            <div className={styles.formFieldContainer}>
                                                <FormFieldTextArea handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleThirdPartyContactChange(index, 'additional_information', event.currentTarget.value)} formFieldId={`thirdPartyContactAdditionalInformation${index}`} formFieldLabel={`Additional Information`} tooltip="Any additional information for the contact at this third party." value={thirdPartyContact.additional_information || ''} />
                                            </div>
                                        </Fragment>
                                    ))}
                                    <div className={styles.formFieldContainerBottom}>
                                        <Button variant="linkText" size="lg" onClick={handleAddThirdPartyContact}>
                                            {'+ Add Contact'}
                                        </Button>
                                    </div>
                                    <div className={'modalFormButtonContainer'}>
                                        {props.thirdParty && (
                                            <div className={styles.deleteButton}>
                                                <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', displayedModal: 'confirmDelete' })} variant="danger" isLoading={false} loadingText="Deleting...">
                                                    Delete
                                                </Button>
                                            </div>
                                        )}
                                        {saveButtons}
                                    </div>
                                </fieldset>
                            </Form>
                        ),
                    },
                ]}
            />
        </>
    );
};
